<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, onMounted, onBeforeUnmount } from 'vue'
import { ChevronDownIcon, XMarkIcon } from '../icons/Icons'
import CheckBoxInput from '@/common/components/elements/input/CheckBoxInput.vue'

interface Item {
  label: string
  value: any
}

const props = defineProps<{
  items: Item[]
  selectedItems: any[]
  label: string
  multiple?: boolean
  placeholder?: string
  defaultAll?: boolean
}>()

const emits = defineEmits<{
  (e: 'update:selectedItems', selected: any[]): void
}>()

const isOpen = ref(false)
const isButtonMousedOver = ref(false)

// Toggle dropdown visibility
const toggleDropdown = () => {
  isOpen.value = !isOpen.value
  if (isOpen.value === false) {
    filterText.value = ''
    if (filterInputRef.value) {
      filterInputRef.value.blur()
    }
  }
}

// Close dropdown when clicking outside
const dropdownRef = ref<HTMLElement | null>(null)
const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node) && isOpen.value === true) {
    toggleDropdown()
  }
}

// Filter options in menu
const filterInputRef = ref()
const filterText = ref('')
const filteredItems = computed(() => {
  if (!props.items) return []
  return props.items.filter((item) => item.label?.toLocaleLowerCase()?.includes(filterText.value?.toLocaleLowerCase()))
})

// Check if an item is selected
const isSelected = (item: Item) => props.selectedItems?.includes(item.value)

// Computed property for selected labels; used when label isn't provided
const selectedLabels = computed(() => {
  if (!props.items) return ''
  return props.items
    .filter((item) => props.selectedItems?.includes(item.value))
    .map((item) => item.label)
    .join(', ')
})

const isAnItemSelected = computed(() => {
  if (!props.items || !props.selectedItems) return false
  const selectedCount = props.selectedItems.length
  if (props.defaultAll) {
    return selectedCount > 0 && selectedCount < props.items.length
  }
  return selectedCount > 0
})
const placeholderText = computed(() => {
  if (isOpen.value === true) return 'Search...'
  return props.label || selectedLabels.value
})

const toggleTextClass = computed<string>(() => {
  let classesArray = []
  if (isOpen.value === true) classesArray.push('placeholder-gray-400', 'bg-gray-300')
  else if (!isOpen.value === true && isAnItemSelected.value === true)
    classesArray.push('placeholder-white', 'bg-transparent')
  else classesArray.push('placeholder-gray-700', 'bg-transparent')
  return classesArray.join(' ')
})

const toggleIconClass = computed(() => {
  let classesArray = []
  if (isOpen.value) classesArray.push('rotate-180 text-white')
  if (isOpen.value || isButtonMousedOver.value) classesArray.push('text-white')
  return classesArray.join(' ')
})

const handleMouseEnter = () => {
  isButtonMousedOver.value = true
}

const handleMouseLeave = () => {
  isButtonMousedOver.value = false
}

// Toggle selection of an item
const toggleSelection = (item: Item) => {
  let newSelected: any[] = []

  if (props.multiple) {
    if (isSelected(item)) {
      newSelected = props.selectedItems.filter((value) => value !== item.value)
    } else {
      newSelected = [...(props.selectedItems || []), item.value]
    }
  } else {
    if (isSelected(item)) {
      newSelected = []
    } else {
      newSelected = [item.value]
    }
    isOpen.value = false // Close dropdown on single selection
  }
  emits('update:selectedItems', newSelected)
}

const handleClearButtonClick = () => {
  clearSelections()
}

const clearSelections = () => {
  if (props.defaultAll) {
    emits('update:selectedItems', props.items.map(item => item.value))
  } else {
    emits('update:selectedItems', [])
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  filterInputRef.value = null
  dropdownRef.value = null
})
</script>

<template>
  <div
    v-if="isVisible"
    ref="dropdownRef"
    class="relative w-full"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <div class="w-full px-2 py-2 flex items-center justify-between text-left">
      <input
        ref="filterInputRef"
        v-model="filterText"
        type="text"
        class="w-full px-2 py-2 text-gray-700 hover:cursor-pointer"
        :class="toggleTextClass"
        :placeholder="placeholderText"
        @click="toggleDropdown"
      />
      <span
        v-if="isAnItemSelected === false"
        class="px-1 w-6 flex items-center justify-center hover:cursor-pointer"
        :class="toggleIconClass"
        @click="toggleDropdown"
      >
        <ChevronDownIcon />
      </span>
      <span v-else class="px-1 text-white" @click="handleClearButtonClick"> <XMarkIcon /> </span>
    </div>
    <transition name="fade" mode="out-in">
      <div
        v-if="isOpen"
        class="absolute z-20 w-fit mt-2 ml-2 bg-zinc-200 border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto custom-scroll min-w-[12rem] w-auto"
      >
        <ul v-if="filteredItems.length > 0" class="py-1">
          <li
            v-for="item in filteredItems"
            :key="item.value"
            class="w-full flex items-center px-4 py-2 text-gray-700 hover:bg-blue-100 hover:text-blue-600 transition-all duration-300 ease-in-out cursor-pointer"
          >
            <CheckBoxInput :label="item.label" :checked="isSelected(item)" @toggle="toggleSelection(item)" />
          </li>
        </ul>
        <span v-else class="w-full flex items-center justify-center text-sm px-4 py-2 text-gray-700">
          No matching data
        </span>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

/* Custom scrollbar for better aesthetics */
.custom-scroll::-webkit-scrollbar {
  width: 6px;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background-color: #d1d5db;
  border-radius: 10px;
}

.custom-scroll:hover::-webkit-scrollbar-thumb {
  background-color: #9ca3af;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
